<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { Ellipsis } from 'lucide';
import { PaginationEllipsis, PaginationList, PaginationListItem } from 'reka-ui';
import { computed } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphIcon } from '@/components/base/icon';
import { useI18n } from '@/composables/i18n';
import { cn } from '@/utils/shadcn/lib/utils';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<{
    page: number
    /**
     * Off swaps the list in place instead of animating it, for a change that is a rebuild rather
     * than a step through the pages — see the page size watcher in `Pagination.vue`.
     */
    animated?: boolean
    class?: HTMLAttributes['class']
}>(), {
    animated: true,
});

const { i18n } = useI18n();

// Only reached when the custom property below is missing, e.g. with the styles not loaded.
const FALLBACK_TRANSITION_MS = 120;

type PageItem = { type: 'ellipsis' } | { type: 'page', value: number };

/** There are at most two ellipses, one per end of the list. */
function ellipsisSide(index: number, count: number): 'start' | 'end' {
    return index * 2 < count ? 'start' : 'end';
}

/**
 * Identity, not position: a page keeps its key while it stays listed, so a shifting window
 * moves it instead of replacing it. The ellipses are keyed by the side they sit on for the same
 * reason — an index would make the left one leave and re-enter on every step.
 */
function itemKey(item: PageItem, index: number, count: number): string {
    return item.type === 'page' ? `page-${item.value}` : `ellipsis-${ellipsisSide(index, count)}`;
}

/**
 * The end of the list a page belongs to, so it can enter and leave through that edge instead of
 * materialising in place. The current page itself has no side and simply fades.
 */
function side(value: number): 'start' | 'end' | undefined {
    if (value === props.page) {
        return undefined;
    }

    return value < props.page ? 'start' : 'end';
}

/**
 * A leaving item has to drop out of the flow before the survivors are measured, or they see no
 * gap to close and never move. Absolute alone is not enough: an out-of-flow flex child snaps to
 * the container's start corner, so pin it where it stood first.
 */
function onBeforeLeave(el: Element): void {
    if (!props.animated) {
        return;
    }

    const item = el as HTMLElement;
    const parent = item.parentElement;

    if (!parent) {
        return;
    }

    const rect = item.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    item.style.width = `${rect.width}px`;
    item.style.height = `${rect.height}px`;
    item.style.left = `${rect.left - parentRect.left}px`;
    item.style.top = `${rect.top - parentRect.top}px`;
    item.style.position = 'absolute';
}

/**
 * The stylesheet owns the tempo, so the timer below reads it back rather than repeating it —
 * `--page-transition-duration` can be overridden from the outside and the timer has to follow.
 */
function transitionMs(el: Element): number {
    const declared = getComputedStyle(el).getPropertyValue('--page-transition-duration').trim();
    const ms = declared.endsWith('ms') ? Number.parseFloat(declared) : Number.parseFloat(declared) * 1000;

    return Number.isFinite(ms) ? ms : FALLBACK_TRANSITION_MS;
}

/**
 * `transitionend` retires the pinned item. The timer is the safety net: no event ever arrives
 * when the transition is skipped, and the ghost would then sit in the DOM for good.
 */
function onLeave(el: Element, done: () => void): void {
    // Taking `done` makes Vue wait for it rather than timing the leave itself, so an unanimated
    // list has to retire the item here — nothing else would, and it would hang around pinned.
    if (!props.animated) {
        done();
        return;
    }

    let timer = 0;

    const settle = (): void => {
        window.clearTimeout(timer);
        el.removeEventListener('transitionend', settle);
        done();
    };

    timer = window.setTimeout(settle, transitionMs(el) + 50);
    el.addEventListener('transitionend', settle);
}

/** `page-off` has no CSS, which is what leaves enter, leave and the move FLIP inert. */
const transitionName = computed(() => (props.animated ? 'page' : 'page-off'));
</script>

<template>
    <PaginationList
        v-slot="{ items }"
        :class="cn('page-list relative flex items-center gap-1', props.class)"
    >
        <TransitionGroup
            :name="transitionName"
            @before-leave="onBeforeLeave"
            @leave="onLeave"
        >
            <template
                v-for="(item, index) in items"
                :key="itemKey(item, index, items.length)"
            >
                <PaginationListItem
                    v-if="item.type === 'page'"
                    :value="item.value"
                    as-child
                >
                    <SphButton
                        size="sm"
                        variant="ghost"
                        :active="item.value === props.page"
                        :aria-label="i18n.t('Seite %{page}', { page: item.value })"
                        :aria-current="item.value === props.page ? 'page' : undefined"
                        :data-side="side(item.value)"
                        no-min-width
                        no-active-effect
                        class="min-w-(--button-height-sm) mt-px! px-2 text-foreground rounded-none text-sm! leading-none! border-b-2 border-l-0 border-r-0 border-t-0 [&:hover:not(.active)]:border-b-border! [&.active]:border-b-accent!"
                    >
                        {{ item.value }}
                    </SphButton>
                </PaginationListItem>
                <PaginationEllipsis
                    v-else
                    :data-side="ellipsisSide(index, items.length)"
                    class="ellipsis text-muted-foreground flex h-(--button-height-sm) items-center justify-center"
                >
                    <SphIcon
                        :icon="Ellipsis"
                        size="xs"
                        class="shrink-0 translate-y-[25%]"
                    />
                </PaginationEllipsis>
            </template>
        </TransitionGroup>
    </PaginationList>
</template>

<style scoped>
/* The tempo of the whole list, in one place. They are plain custom properties on the root, so a
   caller can retune them through the `class` prop — `[--page-transition-duration:200ms]`. */
.page-list {
    --page-transition-duration: 300ms;
    --page-transition-easing: var(--ease-out-quad);
    /* How far an arriving or departing page reaches past the edge it belongs to. */
    --page-transition-shift: 0.5rem;
}

/* One tempo for every item: it fades in or out while its neighbours slide into the slot the
   change opened up. */
.page-enter-active,
.page-leave-active {
    transition:
        opacity var(--page-transition-duration) var(--page-transition-easing),
        transform var(--page-transition-duration) var(--page-transition-easing);
}

.page-enter-from,
.page-leave-to {
    opacity: 0;
}

/* A page arrives and departs through its own end of the list: the low numbers off to the left,
   the high ones off to the right. The current page has no end to come from and only fades. */
.page-enter-from[data-side='start'],
.page-leave-to[data-side='start'] {
    transform: translateX(calc(-1 * var(--page-transition-shift)));
}

.page-enter-from[data-side='end'],
.page-leave-to[data-side='end'] {
    transform: translateX(var(--page-transition-shift));
}

.page-move {
    transition: transform var(--page-transition-duration) var(--page-transition-easing);
}

/* Exactly one page slot wide, which is what keeps the row's total width constant while a page
   button is traded for an ellipsis. It matters because the row is not always left-aligned: pin it
   to the right and any change in its width drags the left edge, so the first page — which never
   enters, leaves or reorders — would still slide. Page numbers wider than the slot
   (`mode="pages"` past ten pages) still nudge it. */
.ellipsis {
    width: var(--button-height-sm);
}

/* The cross-fade stands in for the movement, at the tempo the caller set. Keeping it is also what
   keeps `transitionend` firing, which is what retires a leaving item. */
@media (prefers-reduced-motion: reduce) {
    .page-list {
        --page-transition-shift: 0;
    }

    .page-move {
        transition: none;
    }
}
</style>
